import { Fieldset, Label, Radios } from "nhsuk-react-components"
import { useContext, useState } from "react"
import { REQUIRED_ERROR_DEFINITION_URL } from "../../constants/customExtensionUrls"
import { QuestionnaireContext } from "../../context/QuestionnaireContext"
import { getBooleanValueFromResponse, getResponseForQuestion } from "../../fhirUtils/questionnaireUtils"
import type { QuestionProps } from "./Question"
import { questionClass } from "./questionClass"
import { QuestionnaireError } from "./QuestionnaireError"
import { showQuestion } from "./showQuestion"

export function BooleanQuestion({ question, saveResponses, topLevel }: QuestionProps): JSX.Element {
  // Questionnaire Context
  const questionnaireContext = useContext(QuestionnaireContext)
  if (!questionnaireContext) {
    throw new Error("BooleanQuestion must be used within a QuestionnaireProvider")
  }
  const { responses, errors } = questionnaireContext

  const [answer, setAnswer] = useState<string>(getBooleanValueFromResponse(getResponseForQuestion(question, responses)))
  const updateAnswers = (updatedAnswer: string): void => {
    setAnswer(updatedAnswer)
    saveResponses({
      linkId: question.linkId,
      text: question.text,
      answer: [
        {
          valueBoolean: updatedAnswer === "yes",
        },
      ],
    })
  }

  const showRequiredError = !!errors[question.linkId]?.required

  const requiredErrorMessage = showRequiredError
    ? (question.extension?.find((ext) => ext.url === REQUIRED_ERROR_DEFINITION_URL)?.valueMarkdown ??
      "This field is required.")
    : null

  return (
    <>
      {showRequiredError && !answer && (
        <QuestionnaireError errorMessage={requiredErrorMessage} formElementId={question.linkId} />
      )}
      <div aria-live="assertive">
        {showQuestion(question, responses) && (
          <Fieldset className={questionClass(question, responses)}>
            {topLevel ? (
              <Fieldset.Legend isPageHeading>{question.text}</Fieldset.Legend>
            ) : (
              <Label>{question.text}</Label>
            )}
            <Radios id={question.linkId} error={showRequiredError}>
              <Radios.Radio
                id="example-1"
                value="yes"
                checked={answer === "yes"}
                onClick={() => {
                  updateAnswers("yes")
                }}
              >
                Yes
              </Radios.Radio>
              <Radios.Radio
                id="example-2"
                value="no"
                checked={answer === "no"}
                onClick={() => {
                  updateAnswers("no")
                }}
              >
                No
              </Radios.Radio>
            </Radios>
          </Fieldset>
        )}
      </div>
    </>
  )
}
